import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Ban, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { verifieer } from '../services/kwaliteitspaspoortApi';

const fmtGetal = (n: number) => n.toLocaleString('nl-NL', { maximumFractionDigits: 1 });
const fmtDag = (iso: string) =>
  new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });

const STATUS_KLEUR: Record<string, string> = {
  actief: 'bg-green-50 text-green-700',
  geldig: 'bg-green-50 text-green-700',
  uitgeschreven: 'bg-red-50 text-red-700',
  verlopen: 'bg-red-50 text-red-700',
  onbekend: 'bg-amber-50 text-amber-700',
};

/** Publieke QR-bestemming van het Belagom Kwaliteitspaspoort (geen login). */
export const PaspoortVerificatiePage: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const { t } = useTranslation('pages');
  const { data, isLoading, isError } = useQuery({ queryKey: ['paspoort-verificatie', code], queryFn: () => verifieer(code), retry: false });

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-xl">
        <div className="flex items-center gap-2 text-[#0B1F3A]">
          <ShieldCheck size={22} />
          <h1 className="text-lg font-bold">{t('screening.paspoort.verify.title')}</h1>
        </div>

        {isLoading ? (
          <div className="mt-8 flex justify-center text-gray-400">
            <Loader2 className="animate-spin" />
          </div>
        ) : isError || !data ? (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
            <div>
              <p className="text-sm font-semibold text-red-800">{t('screening.paspoort.verify.unknownTitle')}</p>
              <p className="mt-1 text-xs text-red-700">{t('screening.paspoort.verify.unknownText')}</p>
            </div>
          </div>
        ) : (
          <>
            {data.status === 'geldig' ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-5">
                <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-800">{t('screening.paspoort.verify.validTitle')}</p>
                  <p className="mt-1 text-xs text-green-700">{t('screening.paspoort.verify.validText')}</p>
                </div>
              </div>
            ) : (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
                <Ban size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    {data.status === 'ingetrokken' ? t('screening.paspoort.verify.revokedTitle') : t('screening.paspoort.verify.invalidTitle')}
                  </p>
                  <p className="mt-1 text-xs text-red-700">
                    {data.status === 'ingetrokken'
                      ? t(data.ingetrokken_door === 'belagom' ? 'screening.paspoort.verify.revokedByBelagomText' : 'screening.paspoort.verify.revokedText', {
                          date: data.ingetrokken_op ? fmtDag(data.ingetrokken_op) : '',
                        })
                      : t('screening.paspoort.verify.invalidText')}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-4 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
              <p className="text-base font-bold text-gray-800">{data.naam}</p>
              <p className="text-xs text-gray-500">
                {[data.bedrijfsnaam, data.kvk_nummer && `KvK ${data.kvk_nummer}`].filter(Boolean).join(' · ')}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                {t('screening.paspoort.verify.periodLine', {
                  from: fmtDag(data.periode_start),
                  to: fmtDag(data.periode_eind),
                  issued: fmtDag(data.uitgegeven_op),
                })}
              </p>

              <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-blue-50/60 p-3">
                  <dt className="text-[11px] text-gray-500">{t('screening.paspoort.peLabel')}</dt>
                  <dd className="text-xl font-bold text-[#0B1F3A]">{fmtGetal(data.pe_punten)}</dd>
                </div>
                <div className="rounded-lg bg-blue-50/60 p-3">
                  <dt className="text-[11px] text-gray-500">{t('screening.paspoort.intervisionLabel')}</dt>
                  <dd className="text-xl font-bold text-[#0B1F3A]">{t('screening.paspoort.hours', { n: fmtGetal(data.intervisie_uren) })}</dd>
                </div>
                <div className="rounded-lg bg-blue-50/60 p-3">
                  <dt className="text-[11px] text-gray-500">{t('screening.paspoort.practiceLabel')}</dt>
                  <dd className="text-xl font-bold text-[#0B1F3A]">{t('screening.paspoort.hours', { n: fmtGetal(data.praktijk_uren) })}</dd>
                </div>
              </dl>

              {data.intervisie_sessies > 0 && (
                <p className="mt-3 text-xs text-gray-600">
                  {t('screening.paspoort.verify.intervisionLine', { count: data.intervisie_sessies, teams: data.intervisie_sessies_teams })}
                </p>
              )}

              {!!data.intervisie_posten?.length && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-700">{t('screening.paspoort.verify.intervisionSessions.sessions')}</p>
                  <ul className="mt-1 divide-y divide-gray-100 rounded border border-gray-200 text-xs text-gray-600">
                    {data.intervisie_posten.map((p, i) => (
                      <li key={`${p.datum}-${i}`} className="px-2 py-1.5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span>
                            {new Date(`${p.datum}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} ·{' '}
                            {p.teams
                              ? t('screening.paspoort.verify.intervisionSessions.sessionTeams', { pct: fmtGetal(p.aanwezigheid_pct ?? 0) })
                              : t('screening.paspoort.verify.intervisionSessions.sessionManual')}
                            {' · '}
                            {t('screening.paspoort.hours', { n: fmtGetal(p.uren) })}
                          </span>
                          {p.teams && (
                            <span
                              className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                                p.log_klopt === true ? 'bg-green-50 text-green-700' : p.log_klopt === false ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {p.log_klopt === true
                                ? t('screening.paspoort.verify.intervisionSessions.logOk')
                                : p.log_klopt === false
                                  ? t('screening.paspoort.verify.intervisionSessions.logBad')
                                  : t('screening.paspoort.verify.intervisionSessions.logNone')}
                            </span>
                          )}
                        </div>
                        {p.teams_meeting_id && (
                          <div className="mt-0.5 break-all font-mono text-[10px] text-gray-400">
                            {t('screening.paspoort.verify.intervisionSessions.meetingId')} {p.teams_meeting_id}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.pe_per_register.length > 0 && (
                <ul className="mt-3 text-xs text-gray-600">
                  {data.pe_per_register.map((r) => (
                    <li key={r.register}>
                      {r.register}: <span className="font-semibold">{fmtGetal(r.punten)}</span>
                    </li>
                  ))}
                </ul>
              )}

              {data.registercontrole && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-gray-700">{t('screening.paspoort.verify.registerCheck')}</p>
                  <ul className="mt-1 space-y-1 text-xs text-gray-600">
                    <li className="flex flex-wrap items-center gap-2">
                      <span>{t('screening.paspoort.verify.kvk')}{data.registercontrole.kvk.nummer ? ` ${data.registercontrole.kvk.nummer}` : ''}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_KLEUR[data.registercontrole.kvk.status]}`}>
                        {t(`screening.paspoort.verify.kvk_${data.registercontrole.kvk.status}`)}
                      </span>
                    </li>
                    <li className="flex flex-wrap items-center gap-2">
                      <span>
                        {t('screening.paspoort.verify.insurance')}
                        {data.registercontrole.verzekering.verzekeraar ? ` · ${data.registercontrole.verzekering.verzekeraar}` : ''}
                        {data.registercontrole.verzekering.geldig_tot ? ` · t/m ${fmtDag(data.registercontrole.verzekering.geldig_tot)}` : ''}
                      </span>
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_KLEUR[data.registercontrole.verzekering.status]}`}>
                        {t(`screening.paspoort.verify.ins_${data.registercontrole.verzekering.status}`)}
                      </span>
                    </li>
                  </ul>
                  <p className="mt-1 text-[11px] text-gray-400">{t('screening.paspoort.verify.registerCheckHint')}</p>
                </div>
              )}

              {data.rollen.length > 0 && (
                <>
                  <p className="mt-4 text-xs font-semibold text-gray-700">{t('screening.paspoort.verify.roles')}</p>
                  <ul className="mt-1 list-disc pl-5 text-xs text-gray-600">
                    {data.rollen.map((r) => (
                      <li key={`${r.rol}-${r.nummer ?? ''}`}>
                        {r.rol}
                        {r.register ? ` — ${r.register}` : ''}
                        {r.nummer ? ` (${r.nummer})` : ''}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p className="mt-4 break-all font-mono text-[10px] text-gray-400">
                {t('screening.paspoort.verify.seal')}: {data.zegel_hash}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaspoortVerificatiePage;
